import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import apiClient from "@/api/client";
import imageOne from '../../resource/dana/gilanpasa/3.png';
import imageTwo from '../../resource/dana/gilanpasa/10.png';
import imageThree from '../../resource/dana/gilanpasa/2.png';
import imageFour from '../../resource/dana/gilanpasa/8.png';
import BookingCalendar from '../Calendar/BookingCalendar';
import GilanPasaRequestModal from '../Forms/GilanPasaRequestModal'; 
import Swal from 'sweetalert2';

const Gilanpasa = () => {
    const navigate = useNavigate();
    const [bookedDates, setBookedDates] = useState([]);
    const [blockedDates, setBlockedDates] = useState([]);
    const [contactDetails, setContactDetails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);

    // Function to render contact value with appropriate link
    const renderContactValue = (contact) => {
    const value = contact.value;
    const type = contact.type || 'phone';

    switch (type) {
        case 'email':
            return (
                <a
                    href={`mailto:${value}`}
                    style={{ color: '#c49b36', textDecoration: 'none' }}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                    {value}
                </a>
            );
        case 'web':
            return (
                <a
                    href={value}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#c49b36', textDecoration: 'none' }}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                    {value.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                </a>
            );
        case 'phone':
        default:
            const phoneNumber = value.replace(/[^0-9+]/g, '');
            return (
                <a
                    href={`tel:${phoneNumber}`}
                    style={{ color: '#c49b36', textDecoration: 'none' }}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                    {value}
                </a>
            );
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch calendar data
                const calendarResponse = await apiClient.get('/get-gilan-pasa-calendar');
                const calendarData = calendarResponse.data.data;   // depends on your apiClient — see below

                setBookedDates(calendarData.booked || []);
                setBlockedDates(calendarData.blocked || []);

                // Fetch contact details
                const contactResponse = await apiClient.get('/contact-setting');
                const settings = contactResponse.data.settings || {};
                setContactDetails(settings.dana || []);
            } catch (error) {
                console.error("Failed to fetch data:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleClick = (event) => {
        const pathMap = {
            afternoon: '/afternoon',
            gilanpasaRequest: '/gilanpasaRequest'
        };
        const path = event.currentTarget.id;
        const targetPath = pathMap[path];
        if (targetPath) {
            navigate(targetPath);
        }
    };

    const handleRefresh = async () => {
        try {
            const response = await apiClient.get('/get-gilan-pasa-calendar');
            const data = response.data.data;
            setBookedDates(data.booked || []);
            setBlockedDates(data.blocked || []);
        } catch (error) {
            console.error("Failed to refresh Gilanpasa dates:", error);
        }
    };

    const handleDateClick = (date, _filterType) => {
        // Guard: booked or blocked? (defensive — calendar already disables these)
        if (bookedDates.some(b => (b.date || b.dana_event_date) === date)) {
            Swal.fire({
                icon: 'warning',
                title: 'Date Unavailable',
                text: 'This date is already booked for Gilan Pasa. Please select another date.',
                confirmButtonColor: '#c49b36',
            });
            return;
        }

        if (blockedDates.some(b => (b.date || b.dana_event_date) === date)) {
            Swal.fire({
                icon: 'info',
                title: 'Date Blocked',
                text: 'This date is blocked for Gilan Pasa. Please select another date.',
                confirmButtonColor: '#c49b36',
            });
            return;
        }

        setSelectedDate(date);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedDate(null);
    };

    const handleSuccessfulSubmit = async () => {
        await Swal.fire({
            icon: 'success',
            title: 'Request Submitted!',
            text: 'Your Gilan Pasa request has been received. The calendar will now update.',
            confirmButtonColor: '#c49b36',
            timer: 2000,
            showConfirmButton: true,
        });
        await handleRefresh();
        handleCloseModal();
    };

    const handleBookingError = async (errorMessage) => {
        await handleRefresh();
        Swal.fire({
            icon: 'info',
            title: 'Booking Failed',
            text: errorMessage + ' The calendar has been updated to show current availability.',
            confirmButtonColor: '#c49b36',
        });
    };

    if (loading) {
        return (
            <div className="danaPayment-page inside-pages">
                <div className="right-box">
                    <img src={imageOne} alt="Right Top Image" className="right-image" width={300} />
                </div>
                <div className="text-center p-4">
                    <h1 className="font-main dark-gold p-4">Loading calendar data...</h1>
                </div>
            </div>
        );
    }

    return (
        <div className="danaPayment-page inside-pages">
            <div className="right-box">
                <img src={imageOne} alt="Right Top Image" className="right-image" width={300} />
            </div>

            <h1 className='font-main dark-gold p-4'>Gilanpasa</h1>
            <p className="h4 dark-gold p-4 mt-4">Permissible Food and Drinks for Buddhist Monastics After Noon</p>

            <p className="h4 p-4 mt-6 ">
                In accordance with the disciplinary rules outlined in the Vinaya Pitaka, Buddhist monks and nuns are permitted to consume meals only between sunrise and noon. After this period, from noon until the following morning, they are allowed to consume only permissible food and drinks, collectively known as Gilanpasa.
            </p>
            <p className="h4 p-4 mt-5">
                Offering Gilanpasa to the Triple Gem is one of the oldest and most revered Buddhist practices. It holds significant merit and helps cultivate generosity. This practice also serves as an opportunity for lay Buddhists to venerate the Buddha, Dhamma, and Sangha by offering ceremonial food and drinks after the noon meal.
            </p>
            <p className="h4 p-4 mt-5 ">
                The Gilanpasa rules provide a framework for monks and nuns to consume food and drinks after noon that support both physical health and spiritual discipline. Offering Gilanpasa is not only an act of reverence but also fosters the development of generosity, compassion, and harmony within the Buddhist community.
            </p>

            <h1 className='font-second dark-gold p-4 mt-6 text-center'>Gilanpasa Calendar</h1>
            <div className="calander-box mt-6 p-4">
                <div className="cl-right">
                    <div className="calnder text-center mt-5">
                        <BookingCalendar
                            bookedDates={bookedDates}
                            blockedDates={blockedDates}
                            filterType="all"
                            onDateClick={handleDateClick}
                            showRefreshButton={true}
                            onRefresh={handleRefresh}
                        />
                    </div>
                </div>
            </div>

            <p className="h4 mt-4 p-4">
                The Gilanpasa Calendar for this month is displayed above. Kindly note that the dates marked in yellow are already booked. We would be grateful if a donor could kindly contribute to the Gilanpasa monthly program and select a preferred date. Your support is greatly appreciated.
            </p>

            <div className='btn-group mt-6'>
                <img src={imageFour} alt="Right Top Image" width={300} id="afternoon" onClick={handleClick} className="curser-pointer" />
            </div>

            <div className="mt-6 calander-box align-item-center p-4 text-center">
                <div className="c-details text-left">
                    <h2 className='intro-text'>Contact Details</h2>
                    <div className="mt-4">
                        {contactDetails.length === 0 ? (
                            <p className="font-p" style={{ color: '#999' }}>
                                No contact details available.
                            </p>
                        ) : (
                            contactDetails.map((contact, index) => (
                                <p key={index} className="font-p">
                                    {contact.label}: {renderContactValue(contact)}
                                    {contact.note && <span style={{ color: '#666', fontSize: '0.9rem' }}> ({contact.note})</span>}
                                </p>
                            ))
                        )}
                    </div>
                </div>
                <div className="d-button">
                    <img src={imageTwo} alt="Right Top Image" className="right-image curser-pointer" width={300} id='gilanpasaRequest' onClick={handleClick} />
                </div>
            </div>

            <div className="bottom-box mt-5">
                <img src={imageThree} alt="bottom-image" width={600} />
            </div>

            <GilanPasaRequestModal
                isOpen={modalOpen}
                onClose={handleCloseModal}
                selectedDate={selectedDate}
                onSuccess={handleSuccessfulSubmit}
                onError={handleBookingError}
            />
        </div>
    );
};

export default Gilanpasa;